import { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, Pencil, Power, EyeOff, Package2, DollarSign,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, Textarea, StatusLine,
} from '../components/ui';

/**
 * Club-side management for rentable add-ons sold alongside a court booking:
 * ball baskets, racquet rentals, ball cans, etc. Each line writes to
 * `rental_items` which the customer booking flow reads to populate the
 * upsell row on the court detail screen.
 */

type PriceUnit = 'per_hour' | 'per_piece' | 'per_can';

type Rental = {
  id: string;
  club_id: string;
  name: string;
  description: string | null;
  price_unit: PriceUnit;
  unit_price: number;
  currency: string;
  icon: string | null;
  max_quantity: number | null;
  is_active: boolean;
};

const UNIT_OPTIONS: Array<{ value: PriceUnit; label: string }> = [
  { value: 'per_piece', label: 'per piece' },
  { value: 'per_hour',  label: 'per hour' },
  { value: 'per_can',   label: 'per can' },
];

export default function ClubRentals({ clubId }: { clubId: string }) {
  const [rows, setRows] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Rental | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('rental_items')
      .select('*')
      .eq('club_id', clubId)
      .order('name');
    setRows((data as Rental[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]);

  const toggleActive = async (r: Rental) => {
    await supabase.from('rental_items').update({ is_active: !r.is_active }).eq('id', r.id);
    load();
  };

  const del = async (r: Rental) => {
    if (!confirm(`Delete "${r.name}"? Past bookings that included it are preserved.`)) return;
    await supabase.from('rental_items').delete().eq('id', r.id);
    load();
  };

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title="Rental items"
        subtitle="Ball baskets, racquet rentals, and other add-ons customers can attach to a court booking."
        actions={<Button icon={Plus} onClick={() => setEditing('new')}>Add item</Button>}
      />

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading rental items…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Package2}
            title="No rental items yet"
            description="Offer ball baskets, racquet rentals, and other add-ons so customers can bundle them with a court booking."
            action={<Button icon={Plus} onClick={() => setEditing('new')}>Add your first item</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Unit price</th>
                <th className="text-left px-5 py-3 font-semibold">Max qty</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {r.icon && <span className="text-lg">{r.icon}</span>}
                      <div>
                        <div className="font-medium text-gray-900">{r.name}</div>
                        {r.description && (
                          <div className="text-xs text-gray-500 max-w-md truncate">{r.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-900">
                      {r.currency} {Number(r.unit_price).toFixed(0)}
                    </div>
                    <div className="text-xs text-gray-500">{r.price_unit.replace('_', ' ')}</div>
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.max_quantity ?? '—'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(r)}>
                      {r.is_active
                        ? <Badge tone="green" icon={Power}>Active</Badge>
                        : <Badge tone="gray" icon={EyeOff}>Hidden</Badge>}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditing(r)}>Edit</Button>
                      <Button size="sm" variant="danger" icon={Trash2} onClick={() => del(r)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <RentalForm
          clubId={clubId}
          rental={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function RentalForm({
  clubId, rental, onClose, onSaved,
}: {
  clubId: string;
  rental: Rental | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !rental;
  const [name, setName] = useState(rental?.name ?? '');
  const [description, setDescription] = useState(rental?.description ?? '');
  const [priceUnit, setPriceUnit] = useState<PriceUnit>(rental?.price_unit ?? 'per_piece');
  const [unitPrice, setUnitPrice] = useState(String(rental?.unit_price ?? '20'));
  const [maxQty, setMaxQty] = useState(String(rental?.max_quantity ?? ''));
  const [icon, setIcon] = useState(rental?.icon ?? '');
  const [isActive, setIsActive] = useState(rental?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const payload = {
      club_id: clubId,
      name,
      description: description || null,
      price_unit: priceUnit,
      unit_price: Number(unitPrice),
      icon: icon || null,
      max_quantity: maxQty ? Number(maxQty) : null,
      is_active: isActive,
    };
    const q = isNew
      ? supabase.from('rental_items').insert(payload)
      : supabase.from('rental_items').update(payload).eq('id', rental!.id);
    const { error } = await q;
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  return (
    <Modal
      title={isNew ? 'Add rental item' : `Edit ${rental?.name}`}
      subtitle="Customers can add this to a court booking at checkout."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Field label="Name" className="sm:col-span-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ball basket (80 balls)" />
          </Field>
          <Field label="Emoji" hint="Optional">
            <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🎾" maxLength={4} />
          </Field>
        </div>

        <Field label="Description" hint="Shown to customers above the quantity picker.">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            placeholder="Heavy-duty basket with 80 pressureless balls, returned at end of session." />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Price unit">
            <Select value={priceUnit} onChange={(e) => setPriceUnit(e.target.value as PriceUnit)}>
              {UNIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          <Field label="Price (AED)">
            <div className="relative">
              <DollarSign className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type="number" min="0" step="1" value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)} required className="pl-9" />
            </div>
          </Field>
          <Field label="Max quantity" hint="Leave blank for unlimited">
            <Input type="number" min="1" value={maxQty} onChange={(e) => setMaxQty(e.target.value)} placeholder="e.g. 4" />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 accent-[#2d5016]" />
          Visible to customers
        </label>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Add item' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
